import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { SignupStateService } from '../../services/signup-state.service';
import { S3ImageKeys } from '../../interfaces/signup.interfaces';
import { KycApiService, FacialVerificationResponse } from 'src/app/auth/services/kyc-api.service';
import { IdentityUploadService } from '../../services/identity-upload.service';
import { CompanyRegistrationService } from 'src/app/auth/services/company-registration.service';
import { PASOS_EMPRESA, PASOS_PERSONA } from '../../constants/signup.constants';

/**
 * Configuración de cámara para selfie (siempre frontal)
 */
const SELFIE_CAMERA_CONFIG = {
  facingMode: 'user',  // Siempre cámara frontal
  width: { ideal: 1280, min: 640 },
  height: { ideal: 720, min: 480 },
};

/**
 * Configuración de captura
 */
const CAPTURE_CONFIG = {
  quality: 0.85,
  minQuality: 0.5,
  maxFileSizeMB: 4.5,
  format: 'image/jpeg' as const,
};

/**
 * Coincidencia mínima exigida entre la selfie y la foto del documento.
 *
 * Es una PUERTA, no una sugerencia: por debajo de este valor no se puede continuar.
 * Antes existía una banda intermedia (30-80%) que mostraba "será revisada
 * manualmente" y dejaba avanzar, pero esa revisión manual no existe en el registro:
 * la marca se guardaba solo en memoria del navegador y nadie la leía. Era un bypass.
 */
const SIMILARITY_THRESHOLD = 80;

/** Intentos permitidos antes de ofrecer ayuda por WhatsApp. */
const MAX_VERIFICATION_ATTEMPTS = 3;

/** WhatsApp de soporte (el mismo que usa el navbar del dashboard). */
const SUPPORT_WHATSAPP = '56965474761';

/**
 * Mensajes de error del backend. Cada código tiene texto propio: si un código llega
 * sin entrada acá, el usuario ve el mensaje que arma el backend, que es correcto pero
 * genérico. Mantener sincronizado con `ErrorCode` del KYC backend.
 */
const ERROR_MESSAGES: Record<string, string> = {
  FILE_REQUIRED: 'Debes capturar una selfie para continuar',
  VALIDATION_ERROR: 'Error en los datos enviados. Intenta de nuevo',
  NO_FACE_DETECTED_SOURCE: 'No pudimos detectar tu rostro. Asegúrate de estar bien iluminado y mira directamente a la cámara',
  NO_FACE_DETECTED_TARGET: 'No se pudo leer la foto del documento. Vuelve al paso anterior para capturar nuevamente',
  AWS_SERVICE_ERROR: 'Error del servidor. Intenta de nuevo',
  AWS_REKOGNITION_ERROR: 'Error analizando la imagen. Intenta de nuevo',
  FACE_VERIFICATION_ERROR: 'Error en la verificación facial',
  SELFIE_SPOOF_DETECTED:
    'La selfie debe ser una foto tomada en el momento con la cámara. No sirve fotografiar una pantalla, una foto impresa ni otra imagen.',
};

/**
 * Códigos cuyo mensaje del backend es MÁS específico que la entrada local, así que se
 * prefiere el del backend. El KYC devuelve un texto distinto por cada motivo de
 * rechazo de liveness (pantalla, impresa, varias personas, sin rostro, score bajo);
 * duplicar esos textos acá crearía dos fuentes de verdad que se desincronizan.
 */
const PREFER_BACKEND_MESSAGE = new Set(['SELFIE_SPOOF_DETECTED']);

type VerificationStatus = 'idle' | 'capturing' | 'preview' | 'analyzing' | 'success' | 'error';

@Component({
  selector: 'app-face-capture-step',
  templateUrl: './face-capture-step.component.html',
  styleUrls: ['./face-capture-step.component.sass'],
})
export class FaceCaptureStepComponent implements OnInit, OnDestroy {
  @Output() onError = new EventEmitter<string>();
  @Output() onSuccess = new EventEmitter<string>();

  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;

  // Estado del componente
  status: VerificationStatus = 'idle';
  selfieImage: string = '';
  showCameraModal: boolean = false;
  loading: boolean = false;
  isReadyToCapture: boolean = false;

  // Resultado de verificación
  verificationMessage: string = '';

  /** Intentos fallidos de verificación en este paso. */
  attempts: number = 0;

  // Stream de cámara
  private stream: MediaStream | null = null;

  constructor(
    public signupState: SignupStateService,
    private kycService: KycApiService,
    private identityUploadService: IdentityUploadService,
    private companyService: CompanyRegistrationService
  ) {}

  ngOnInit(): void {
    // Verificar que tengamos la foto del documento del paso anterior (OCR)
    if (!this.signupState.hasFotoPersona) {
      this.onError.emit('No se encontró la foto del documento. Volviendo al paso de captura.');
      // Redirigir al paso de OCR para re-capturar
      this.signupState.goToStep(
        this.signupState.isEmpresa ? PASOS_EMPRESA.OCR : PASOS_PERSONA.OCR
      );
    }
  }

  ngOnDestroy(): void {
    this.cleanupCamera();
  }

  // ==================== CÁMARA ====================

  async openCameraModal(event?: Event): Promise<void> {
    if (event) event.preventDefault();
    this.loading = true;
    this.showCameraModal = true;
    this.status = 'capturing';
    this.isReadyToCapture = false;

    setTimeout(async () => {
      if (this.videoElement) {
        await this.initCamera();
      }
      this.loading = false;
    }, 300);
  }

  closeCameraModal(): void {
    this.showCameraModal = false;
    this.cleanupCamera();
    if (this.status === 'capturing') {
      this.status = 'idle';
    }
  }

  private cleanupCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    const video = this.videoElement?.nativeElement;
    if (video) {
      video.srcObject = null;
      video.load();
    }
  }

  private async initCamera(): Promise<void> {
    const video = this.videoElement.nativeElement as HTMLVideoElement;

    try {
      const constraints: MediaStreamConstraints = {
        video: SELFIE_CAMERA_CONFIG,
        audio: false,
      };

      this.stream = await navigator.mediaDevices.getUserMedia(constraints);

      video.srcObject = this.stream;
      video.setAttribute('playsinline', 'true');
      video.setAttribute('autoplay', 'true');
      video.muted = true;

      await video.play();

      // Aplicar configuración óptima
      await this.applyOptimalSettings(this.stream);

      this.isReadyToCapture = true;

    } catch (err) {
      this.onError.emit('No se pudo acceder a la cámara. Verifica los permisos.');
      this.closeCameraModal();
    }
  }

  private async applyOptimalSettings(stream: MediaStream): Promise<void> {
    try {
      const [track] = stream.getVideoTracks();
      const capabilities = track.getCapabilities() as any;
      const settings: MediaTrackConstraintSet[] = [];

      // Foco continuo
      if (capabilities.focusMode?.includes('continuous')) {
        settings.push({ focusMode: 'continuous' } as any);
      }

      // Exposición automática
      if (capabilities.exposureMode?.includes('continuous')) {
        settings.push({ exposureMode: 'continuous' } as any);
      }

      if (settings.length > 0) {
        await track.applyConstraints({ advanced: settings });
      }
    } catch (err) {
      // Ajustes no críticos
    }
  }

  // ==================== CAPTURA ====================

  captureSelfie(): void {
    const video = this.videoElement.nativeElement as HTMLVideoElement;

    if (!video || video.videoWidth === 0) {
      this.onError.emit('La cámara no está lista. Intenta de nuevo.');
      return;
    }

    try {
      // Capturar frame del video
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No se pudo crear contexto de canvas');

      // Dibujar frame (sin mirror para selfie natural)
      ctx.drawImage(video, 0, 0);

      // Comprimir imagen
      this.selfieImage = this.compressImage(canvas);

      // Cerrar cámara y mostrar preview
      this.closeCameraModal();
      this.status = 'preview';

    } catch (err) {
      this.onError.emit('Error al capturar imagen. Intenta de nuevo.');
    }
  }

  private compressImage(canvas: HTMLCanvasElement): string {
    let quality = CAPTURE_CONFIG.quality;
    let result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);

    // Reducir calidad hasta cumplir límite
    while (
      this.getFileSizeMB(result) > CAPTURE_CONFIG.maxFileSizeMB &&
      quality > CAPTURE_CONFIG.minQuality
    ) {
      quality -= 0.05;
      result = canvas.toDataURL(CAPTURE_CONFIG.format, quality);
    }

    return result;
  }

  private getFileSizeMB(base64: string): number {
    const base64Data = base64.split(',')[1] || '';
    return (base64Data.length * 0.75) / (1024 * 1024);
  }

  retakeSelfie(): void {
    this.selfieImage = '';
    this.status = 'idle';
    this.verificationMessage = '';
  }

  // ==================== VERIFICACIÓN ====================

  async confirmSelfie(): Promise<void> {
    if (!this.selfieImage) return;

    this.status = 'analyzing';
    this.loading = true;

    try {
      const fotoDocumento = this.signupState.fotoPersonaFromOcr;

      if (!fotoDocumento) {
        throw { code: 'NO_FACE_DETECTED_TARGET', message: 'No se encontró la foto del documento' };
      }

      // Convertir selfie a File
      const selfieFile = this.base64ToFile(this.selfieImage, 'selfie.jpg');

      // Llamar al servicio de verificación facial
      const result = await this.kycService.verifyFace(
        selfieFile,
        fotoDocumento,
        SIMILARITY_THRESHOLD
      );


      // Evaluar resultado y subir imágenes a S3 si es exitoso
      await this.processVerificationResult(result);

    } catch (error: any) {
      this.handleVerificationError(error);
    } finally {
      this.loading = false;
    }
  }

  private async processVerificationResult(result: FacialVerificationResponse): Promise<void> {
    // Verificar detección de rostros
    if (!result.sourceFaceDetected) {
      this.status = 'error';
      this.verificationMessage = ERROR_MESSAGES['NO_FACE_DETECTED_SOURCE'];
      return;
    }

    if (!result.targetFaceDetected) {
      this.status = 'error';
      this.verificationMessage = ERROR_MESSAGES['NO_FACE_DETECTED_TARGET'];
      return;
    }

    // El liveness lo decide el backend: si bloqueó, este método no se ejecuta (el
    // endpoint responde 400 y lo maneja handleVerificationError).
    //
    // Las observaciones de liveness NO se loguean acá a propósito: los motivos dicen
    // qué señal detectó el ataque ("Dispositivo/pantalla detectado: Phone 99.7%"), y
    // exponerlos en la consola del navegador le daría al atacante el mapa para
    // evadirlos. El backend ya los registra del lado servidor (SELFIE_LIVENESS_AUDIT).

    // Puerta única: la coincidencia con el documento debe llegar al umbral.
    if (result.similarity >= SIMILARITY_THRESHOLD) {
      await this.handleSuccess(result);
    } else {
      this.handleMismatch(result);
    }
  }

  private async handleSuccess(result: FacialVerificationResponse): Promise<void> {
    this.status = 'success';
    // Los dos flujos suben las imágenes en este paso, así que el mensaje es el
    // mismo: antes empresa decía "exitosa" a secas porque no subía nada acá.
    this.verificationMessage = 'Verificación exitosa. Guardando imágenes...';

    // Guardar imágenes. La marca de verificado se pone DESPUES de que las
    // imagenes esten guardadas: si se pusiera antes y la subida fallara, el
    // estado diria que la identidad esta resuelta sin tener nada archivado, y
    // el paso del e-RUT dejaria pasar a alguien sin `s3Keys`.
    try {
      this.signupState.setSelfieImage(this.selfieImage);
      await this.persistIdentityImages();

      this.signupState.setFaceVerified(true);
      this.signupState.setFaceVerificationResult({
        isMatch: true,
        similarity: result.similarity,
        needsManualReview: false,
      });

      this.verificationMessage = 'Verificación exitosa';
      this.onSuccess.emit('Verificación facial completada exitosamente');

      // Auto-avanzar después de 2.5 segundos
      setTimeout(() => {
        this.signupState.nextStep();
      }, 2500);
    } catch (error: any) {
      // El backend valida el liveness de la selfie antes de guardarla, así que este
      // catch no siempre es "no se pudo guardar": puede traer un motivo concreto de
      // rechazo. Se muestra ese texto en lugar de un genérico que confundiría.
      const detail: string | undefined = error?.detail;
      this.signupState.resetFaceVerification();
      this.status = 'error';
      this.verificationMessage = detail || 'Error al guardar las imágenes. Intenta nuevamente.';
      this.onError.emit(detail || 'Error al guardar las imágenes');

      // Si el rechazo es corregible por el usuario, le cuenta como intento para que
      // llegue al mensaje de soporte y no quede en un bucle.
      if (detail) {
        this.attempts += 1;
      }
    }
  }

  private handleMismatch(result: FacialVerificationResponse): void {
    // Si la selfie no daba para comparar —lentes de sol, contraluz, rostro chico,
    // movida— se muestra ESE motivo. Decirle "no coincidís" a alguien cuya foto no
    // se podía evaluar es falso, y lo deja repitiendo el mismo error hasta agotar
    // los intentos. El texto lo arma el KYC, que es donde vive el análisis.
    const motivoDeCalidad = result.calidadSelfie?.mensaje;

    // El porcentaje de coincidencia no se le muestra al usuario: es un dato del
    // motor de comparación y saber qué tan cerca quedó del umbral le sirve sobre
    // todo a quien está probando con una foto ajena.
    this.registerFailedAttempt(
      motivoDeCalidad
        ? motivoDeCalidad
        : `Tu rostro no coincide lo suficiente con la foto del documento. ` +
            `Inténtalo de nuevo con buena luz y sin lentes.`
    );

    // No se guarda nada en el estado: hay que reintentar.
    this.signupState.setFaceVerificationResult({
      isMatch: false,
      similarity: result.similarity,
      needsManualReview: false,
    });
  }

  private handleVerificationError(error: any): void {
    const code = error?.code || 'FACE_VERIFICATION_ERROR';
    const backendMessage = error?.message || 'Error desconocido';

    const message = PREFER_BACKEND_MESSAGE.has(code)
      ? backendMessage || ERROR_MESSAGES[code]
      : ERROR_MESSAGES[code] || backendMessage;

    // Los errores del propio servicio (AWS caído, datos inválidos) no son culpa del
    // usuario y no deben consumirle intentos.
    const isUserFixable = code === 'SELFIE_SPOOF_DETECTED' || code === 'NO_FACE_DETECTED_SOURCE';

    if (isUserFixable) {
      this.registerFailedAttempt(message);
    } else {
      this.status = 'error';
      this.verificationMessage = message;
    }
  }

  /**
   * Cuenta el intento fallido y, al llegar al máximo, ofrece ayuda por WhatsApp en
   * lugar de dejar al usuario en un bucle de "intentar de nuevo".
   */
  private registerFailedAttempt(message: string): void {
    this.attempts += 1;
    this.status = 'error';
    this.verificationMessage = message;
  }

  /** true cuando se agotaron los intentos y hay que ofrecer soporte. */
  get attemptsExhausted(): boolean {
    return this.attempts >= MAX_VERIFICATION_ATTEMPTS;
  }

  /** Abre WhatsApp de soporte con un mensaje precargado. */
  contactSupport(): void {
    const text = encodeURIComponent(
      'Hola, no pude completar la verificación de identidad en el registro y necesito ayuda.'
    );
    window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${text}`, '_blank');
  }

  private base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  }

  // ==================== SUBIDA S3 ====================

  /**
   * Guarda las imágenes de identidad según el tipo de cuenta.
   *
   * Los dos casos suben ahora y dejan las rutas en `s3Keys`. Cambian solo el
   * endpoint y los nombres de archivo.
   *
   * Empresa subía antes en el paso de la declaración PEP, junto con el
   * formulario del representante legal. Esperar tres pantallas costaba caro:
   * las base64 no se persisten, así que un refresh en cualquiera de ellas
   * obligaba a rehacer documento y selfie, y al reintentar sin imágenes en
   * memoria `data4` se salteaba. Con las rutas guardadas acá, ese paso solo
   * las referencia.
   */
  private async persistIdentityImages(): Promise<void> {
    if (this.signupState.isEmpresa) {
      await this.subirIdentidadDeEmpresa();
      return;
    }

    await this.uploadImagesToS3();
  }

  /**
   * Sube documento y selfie del representante legal, de a un archivo.
   *
   * Va por `storage/upload-document` y no por el endpoint de persona porque los
   * nombres de archivo son los que compliance espera encontrar en el registro
   * de empresa (`documento_frontal_representante`, etc.); cambiarlos rompería
   * lo que ya está archivado.
   *
   * `duranteRegistro` en true para que un 401 no se lea como sesión expirada y
   * expulse al usuario al login a mitad del alta.
   */
  private async subirIdentidadDeEmpresa(): Promise<void> {
    const state = this.signupState.currentState;

    if (!state.cognitoId) {
      throw { detail: 'No se encontró el ID de usuario' };
    }
    if (!this.selfieImage) {
      throw { detail: 'No se encontró la imagen de selfie' };
    }
    if (!state.documentImageFront) {
      throw { detail: 'No se encontró la imagen del documento frontal' };
    }
    // Empresa siempre usa documento chileno, que lleva reverso.
    if (!state.documentImageBack) {
      throw { detail: 'No se encontró la imagen del documento posterior' };
    }

    const archivos: { campo: keyof S3ImageKeys; nombre: string; base64: string }[] = [
      {
        campo: 'documentFront',
        nombre: 'documento_frontal_representante',
        base64: state.documentImageFront,
      },
      {
        campo: 'documentBack',
        nombre: 'documento_reverso_representante',
        base64: state.documentImageBack,
      },
      { campo: 'selfie', nombre: 'selfie_representante', base64: this.selfieImage },
    ];

    const keys: S3ImageKeys = { selfie: '', documentFront: '', documentBack: null };

    for (const archivo of archivos) {
      let response: any;
      try {
        response = await this.companyService.sendFile(
          { name: archivo.nombre, base64: archivo.base64, id: state.cognitoId },
          true
        );
      } catch (error: any) {
        // El backend valida el liveness de la selfie antes de guardarla: el
        // mensaje que trae es el motivo real del rechazo y hay que mostrarlo.
        throw { detail: error?.message };
      }

      if (!response?.key) {
        throw { detail: 'No pudimos guardar las imágenes de identidad. Intenta nuevamente.' };
      }

      keys[archivo.campo] = response.key;
    }

    this.signupState.setS3Keys(keys);
  }

  /**
   * Sube las imágenes de identidad a S3
   * - selfie: capturada en este paso
   * - documentFront: capturada en paso OCR
   * - documentBack: solo Chile (capturada en paso OCR)
   */
  private async uploadImagesToS3(): Promise<void> {
    const state = this.signupState.currentState;

    // Verificar que tenemos todos los datos necesarios
    if (!state.cognitoId) {
      throw { detail: 'No se encontró el ID de usuario' };
    }
    if (!this.selfieImage) {
      throw { detail: 'No se encontró la imagen de selfie' };
    }
    if (!state.documentImageFront) {
      throw { detail: 'No se encontró la imagen del documento frontal' };
    }

    // Determinar país (CL o VE)
    const documentType = this.signupState.documentType;
    const country: 'CL' | 'VE' = documentType === 'chile' ? 'CL' : 'VE';

    // Verificar que Chile tenga imagen trasera
    if (country === 'CL' && !state.documentImageBack) {
      throw { detail: 'No se encontró la imagen del documento posterior' };
    }

    // Convertir base64 a File
    const selfieFile = this.base64ToFile(this.selfieImage, 'selfie.jpg');
    const frontFile = this.base64ToFile(state.documentImageFront, 'document-front.jpg');
    const backFile = country === 'CL' && state.documentImageBack
      ? this.base64ToFile(state.documentImageBack, 'document-back.jpg')
      : undefined;

    // Subir a S3
    const response = await this.identityUploadService.uploadIdentityDocuments(
      state.cognitoId,
      country,
      selfieFile,
      frontFile,
      backFile
    );

    if (!response.success) {
      throw { detail: response.message };
    }

    // Guardar rutas S3 en estado
    this.signupState.setS3Keys({
      selfie: response.data.keys.selfie,
      documentFront: response.data.keys.documentFront,
      documentBack: response.data.keys.documentBack,
    });

  }

  // ==================== NAVEGACIÓN ====================

  async goBack(): Promise<void> {
    await this.signupState.prevStep();
  }
}
